import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  styled,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { SignedStatus } from 'src/views/Documents/components/SignedStatus';
import type { InteractionDocument } from '@/common/types/interaction';
import type { OfferEnvelope } from '../types';

const Options = styled(FormGroup)`
  padding-left: ${({ theme }) => `${theme.spacing(4)}`};
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, auto));
  ${({ theme }) => theme.breakpoints.only('xs')} {
    grid-template-columns: auto;
  }
`;

export interface Props {
  envelope: OfferEnvelope;
  isFirstEnvelopeInList: boolean;
  selectedDocuments: string[];
  onEnvelopeSelected: ({
    checked,
    envelopeId,
    envelopeDocs,
  }: {
    checked: boolean;
    envelopeId: string;
    envelopeDocs: (InteractionDocument | null)[];
  }) => void;
  onDocumentSelected: ({
    id,
    checked,
    envelopeId,
  }: {
    id: string;
    checked: boolean;
    envelopeId: string;
  }) => void;
}

export const EnvelopeGroup = ({
  envelope,
  isFirstEnvelopeInList,
  onEnvelopeSelected,
  onDocumentSelected,
  selectedDocuments,
}: Props) => {
  const [indeterminate, setIndeterminate] = useState(false);
  const [envelopeChecked, setEnvelopeChecked] = useState(false);

  const documents = envelope.documents;

  useEffect(() => {
    if (isFirstEnvelopeInList) {
      onEnvelopeSelected({
        checked: true,
        envelopeId: envelope.id,
        envelopeDocs: documents || [],
      });
    }
  }, []);

  useEffect(() => {
    setIndeterminate(
      selectedDocuments.length !== documents.length &&
        selectedDocuments.length !== 0,
    );

    setEnvelopeChecked(selectedDocuments.length === documents.length);
  }, [selectedDocuments, documents]);

  if (!documents.length) return null;

  return (
    <Box sx={{ padding: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <FormControlLabel
          sx={{ pb: { xs: 1, md: 0 } }}
          key={envelope.id}
          control={
            <Checkbox
              id={envelope.id}
              name={`envelope ${envelope.name}`}
              checked={envelopeChecked}
              indeterminate={indeterminate}
              onChange={({ target: { checked } }) => {
                if (!envelope.id) return;
                onEnvelopeSelected({
                  checked,
                  envelopeId: envelope.id,
                  envelopeDocs: documents || [],
                });
              }}
            />
          }
          aria-label={`envelope ${envelope.name}`}
          label={
            <Typography variant="body2">
              {envelope.name ?? 'No Name'}
            </Typography>
          }
        />
        <SignedStatus status={envelope.status} sx={{ mr: 0 }} />
      </Box>
      <Options>
        {documents.map((document) => {
          if (!document?.id) {
            return null;
          }
          return (
            <FormControlLabel
              sx={{ pb: { xs: 1, md: 0 } }}
              key={document.id}
              control={
                <Checkbox
                  id={document.id}
                  name={`document ${document.name}`}
                  checked={
                    !!document.id && !!selectedDocuments.includes(document.id)
                  }
                  onChange={({ target: { checked } }) => {
                    if (!document.id) return;
                    onDocumentSelected({
                      id: document.id,
                      checked,
                      envelopeId: envelope.id,
                    });
                  }}
                />
              }
              aria-label={`document ${document.name}`}
              label={
                <Typography variant="body2">
                  {document.name || 'No Name'}
                </Typography>
              }
            />
          );
        })}
      </Options>
    </Box>
  );
};
